
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useTaskMutations } from '@/hooks/useTaskMutations';
import { supabase } from "@/integrations/supabase/client"; // To get user email

interface QuickAddTaskProps {
  importance: number;
  urgency: number;
  // Add other default props if needed
  disabled?: boolean;
}

const QuickAddTask: React.FC<QuickAddTaskProps> = ({ importance, urgency, disabled = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { addTask } = useTaskMutations();

  // Get user email once on mount
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserEmail(session?.user?.email ?? null);
    };
    getUser();
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSubmit = async () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || !userEmail || disabled) return; // Need content and user and not disabled

    try {
      await addTask.mutateAsync({
        task: trimmedValue,
        importance: importance.toString(),
        urgency: urgency.toString(),
        deadline: '',
        tags: [],
        is_recurring: false,
        recurrence_frequency: null,
      });
      setInputValue(''); // Clear input on success
      // Success toast handled by mutation hook
    } catch (_error) {
      // Error handled by mutation hook
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="relative flex items-center mt-1">
      <Input
        type="text"
        placeholder="+ Add Task"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className="h-8 text-sm pl-2 pr-8 border-dashed border-border/60 focus:border-solid focus:border-primary focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        disabled={disabled || addTask.isPending || !userEmail}
        aria-label="Quick add task"
      />
      {/* Optional: Show button on focus/hover/value, basic always-show for now */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-0 h-7 w-7 text-muted-foreground hover:text-primary disabled:opacity-50"
        onClick={handleSubmit}
        disabled={disabled || !inputValue.trim() || addTask.isPending || !userEmail}
        aria-label="Add task"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default QuickAddTask; 
